import { useCountAllItems } from "@/api/cms";
import { useExportData } from "@/api/export";
import { DropdownMenu } from "@/components/DropdownMenu";
import Header from "@/components/navigation/Header";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import SearchBar from "@/components/ui/searchbar";
import { Separator } from "@/components/ui/separator";
import {
  getCountByStatusByTable,
  getExportConfigByTable,
} from "@/config/dashboard/export";
import { extractLabel } from "@/config/forms/advanced-search/advanced-search-fields-def";
import { useAdvancedSearch } from "@/context/advanced-search/AdvancedSearchContext";
import { useAuth } from "@/context/auth/AuthContext";
import { DashboardParamsProviders } from "@/context/DashboardParamsProviders";
import { useToast } from "@/hooks/use-toast";
import { useDashboardParams } from "@/hooks/useDashboardParams";
import {
  ExportConfig,
  ExportDataDto,
  ExportField,
  ExportGroup,
  ExportType,
} from "@/lib/auth/export/export-types";
import { tabMenuItems, TabMenuItems } from "@/lib/types";
import {
  isInSearch,
  resolveExportTable,
  tabMenuKeyToPublicationStatusField,
  tabMenuKeyToTranslationKey,
} from "@/lib/utils";
import { ChevronLeft } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import {
  FormProvider,
  useForm,
  useFormContext,
  useWatch,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { ExportFieldsList } from "./ExportFieldsList";
import { ExportFilters } from "./ExportFilters";
import { ExportGroupMenu } from "./ExportGroupMenu";
import { LoadExport } from "./ExportModals";
import { ExportSummary } from "./ExportSummary";
import { ExportTypeSelector } from "./ExportTypeSelector";
import { readBoPreference } from "@/lib/user-preferences/store";
import { tWithKey } from "@/lib/i18n-fallback";
import {
  getFormFieldsPreferenceKey,
  type FormFieldsVisibilityState,
} from "@/lib/form-field-visibility";
import { isFieldVisibleInFiche } from "@/config/field-visibility/always-visible-fields";

const getInitialFormConfig = (config: ExportConfig, loadedConfig: ExportConfig | null) =>
  loadedConfig ??
  config.map((elem) => {
    if (elem.type !== "group") {
      return {
        ...elem,
        checked: false,
      };
    }
    return {
      ...elem,
      fields: elem.fields.map((field) => {
        return {
          ...field,
          checked: false,
        };
      }),
    };
  });

/**
 * Rebuilds the export selection from the per-user field-visibility grid:
 * a field is checked iff it is currently shown (expanded) in the fiche.
 * Export-only columns (vignette/thumbnail) have no grid entry → left off.
 */
const applyVisibilityToConfig = (
  config: ExportConfig,
  visibility: FormFieldsVisibilityState,
  table: TabMenuItems,
): ExportConfig =>
  config.map((elem) => {
    if (elem.type !== "group") {
      return {
        ...elem,
        checked:
          elem.type !== "vignette" &&
          isFieldVisibleInFiche(table, elem.key, visibility),
      };
    }
    return {
      ...elem,
      fields: elem.fields.map((field) => ({
        ...field,
        checked:
          field.type !== "vignette" &&
          isFieldVisibleInFiche(table, field.key, visibility),
      })),
    };
  });

export const Export = () => {
  const { state } = useLocation();

  const initialTable = resolveExportTable(state?.prevLocation);

  const [storageKey, setStorageKey] = useState<string>(initialTable);
  const [loadedConfig, setLoadedConfig] = useState<ExportConfig | null>(null);

  return (
    <DashboardParamsProviders
      key={storageKey}
      storageKey={storageKey}
      persistInSearchParams={false}
    >
      <Header />
      <ExportPage
        selectedTable={storageKey as TabMenuItems}
        setSelectedTable={setStorageKey as (key: string) => void}
        loadedConfig={loadedConfig}
        setLoadedConfig={setLoadedConfig}
      />
    </DashboardParamsProviders>
  );
};

const ExportPage = ({
  selectedTable,
  setSelectedTable,
  loadedConfig,
  setLoadedConfig,
}: {
  selectedTable: TabMenuItems;
  setSelectedTable: (key: TabMenuItems) => void;
  loadedConfig: ExportConfig | null;
  setLoadedConfig: (config: ExportConfig | null) => void;
}) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { instanceData } = useAuth();
  const exportConfigByTable = getExportConfigByTable(instanceData);
  const config = exportConfigByTable[selectedTable];
  const { data: countAllItems } = useCountAllItems(selectedTable);
  const advancedSearch = useAdvancedSearch();

  const { filterStatus, currentCount, fetchParams } = useDashboardParams({
    resourceName: selectedTable,
    statusField: tabMenuKeyToPublicationStatusField(selectedTable),
    resourceCounter: getCountByStatusByTable[selectedTable],
  });

  const { mutateAsync: exportData, isPending } = useExportData();

  const [selectedType, setSelectedType] = useState<ExportType>("partial");
  const [search, setSearch] = useState("");
  const [activeGroup, setActiveGroup] = useState<string | null>(null);
  const [useDisplayedFields, setUseDisplayedFields] = useState(false);

  const groupRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const previousResetKeyRef = useRef<string | null>(null);

  const form = useForm<ExportConfig>({
    defaultValues: getInitialFormConfig(config, loadedConfig),
  });

  useEffect(() => {
    const resetKey = `${selectedTable}-${loadedConfig ? "loaded" : "default"}`;
    if (previousResetKeyRef.current === resetKey) return;
    form.reset(getInitialFormConfig(config, loadedConfig));
    previousResetKeyRef.current = resetKey;
  }, [form, config, loadedConfig, selectedTable]);

  useEffect(() => {
    if (activeGroup === null) {
      setActiveGroup(config[0].label ?? null);
    }
  }, [config, activeGroup]);

  const handleSelectTable = (value: TabMenuItems) => {
    setSelectedTable(value);
    groupRefs.current = {};
    setActiveGroup(null);
    setSearch("");
    setLoadedConfig(null);
    setUseDisplayedFields(false);
  };

  const handleToggleDisplayedFields = (checked: boolean) => {
    setUseDisplayedFields(checked);
    if (checked) {
      const visibility =
        readBoPreference<FormFieldsVisibilityState>(
          getFormFieldsPreferenceKey(selectedTable),
        ) ?? {};
      form.reset(applyVisibilityToConfig(config, visibility, selectedTable));
    } else {
      form.reset(getInitialFormConfig(config, loadedConfig));
    }
  };

  const handleLoadExistingExport = (
    config: ExportConfig,
    table: TabMenuItems,
  ) => {
    setSelectedTable(table);
    setLoadedConfig(config);
  };

  const handleSearch = (value: string) => {
    setSearch(value);
  };

  const onSubmit = async (data: ExportConfig) => {
    const exportDataDto: ExportDataDto = {
      config: data,
      table: selectedTable,
      filterStatus,
      exportType: selectedType,
      globalSearch: advancedSearch.globalSearch,
      advancedSearchQuery: advancedSearch.advancedSearchQuery,
      fetchParams: fetchParams,
    };

    await exportData(exportDataDto, {
      onSuccess: (res) => {
        const blob = res.data as Blob;
        const cd = res.headers["content-disposition"] ?? "";
        const match = /filename="?([^";]+)"?/i.exec(cd);
        const filename = match?.[1] ?? `export-${selectedTable}.xlsx`;

        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
      },
      onError: () => {
        toast({
          variant: "destructive",
          title: t("export.error"),
        });
      },
    });
  };

  return (
    <FormProvider {...form}>
      <div className="flex flex-col py-3 items-center w-full bg-white">
        <div className="flex flex-col py-6 gap-6 items-center w-[90%]">
          <div className="flex flex-col gap-6 w-full">
            <Link to={`/dashboard/${selectedTable}`}>
              <Button variant="outline" className="w-fit gap-1">
                <ChevronLeft className="w-4 h-4" />
                <span className="text-sm font-medium">{t("goBack")}</span>
              </Button>
            </Link>
            <div className="flex items-center justify-between w-full">
              <div className="flex flex-col gap-4 w-full">
                <h2 className="text-3xl font-bold">
                  {selectedType === "partial"
                    ? t("export.partial.title")
                    : t("export.full.title")}
                </h2>
                <p>{t("export.tableSelect")}</p>
                <DropdownMenu
                  className="max-w-[40%]"
                  placeholder={t("export.table")}
                  index={tabMenuItems.indexOf(selectedTable)}
                  data={tabMenuItems.map((key) => ({
                    label: t(tabMenuKeyToTranslationKey(key)),
                    value: key,
                  }))}
                  onSelectChange={handleSelectTable as (value: string) => void}
                />
              </div>
              <div className="flex flex-col self-stretch justify-between items-end">
                <LoadExport
                  handleLoadExistingExport={handleLoadExistingExport}
                />
                <div className="flex flex-col items-end gap-3">
                  <ExportTypeSelector
                    selectedType={selectedType}
                    setSelectedType={setSelectedType}
                  />
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={useDisplayedFields}
                      onCheckedChange={(v) =>
                        handleToggleDisplayedFields(v === true)
                      }
                    />
                    {tWithKey(
                      t,
                      "export.useDisplayedFields",
                      "Exporter en fonction des champs affichés",
                    )}
                  </label>
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center justify-between w-full min-h-[30px]">
            {selectedType === "partial" ? (
              <ExportFilters filterStatus={filterStatus} />
            ) : (
              <div className="min-h-[30px]"></div>
            )}
            <p className="text-sm text-gray-700">
              {selectedType === "partial"
                ? currentCount + " " + t("results")
                : countAllItems + " " + t("results")}
            </p>
          </div>
        </div>
        <Separator />
        <div className="flex py-8 gap-4 w-[90%] justify-between">
          <div className="flex flex-col gap-6 w-[70%]">
            <div className="pr-8">
              <SearchBar search={search} onChangeSearch={handleSearch} />
            </div>
            <div className="flex py-4 justify-between">
              <ExportGroupMenu
                config={config}
                activeGroup={activeGroup}
                groupRefs={groupRefs}
              />
              <div className="flex flex-col px-2 gap-6 w-full">
                <div className="flex pb-5 px-8 items-center justify-between">
                  <p className="text-xl font-semibold">
                    {t("export.contentFromTable") +
                      ' "' +
                      t(tabMenuKeyToTranslationKey(selectedTable)) +
                      '"'}
                  </p>
                  <SelectAllCheck search={search} />
                </div>
                <ExportFieldsList
                  groupRefs={groupRefs}
                  search={search}
                  selectedTable={selectedTable}
                  setActiveGroup={setActiveGroup}
                />
              </div>
            </div>
          </div>
          <ExportSummary
            selectedTable={selectedTable}
            onSubmit={form.handleSubmit(onSubmit)}
            numberOfResults={
              selectedType === "partial" ? currentCount : (countAllItems ?? 0)
            }
            isPending={isPending}
          />
        </div>
      </div>
    </FormProvider>
  );
};

type SelectAllCheckProps = {
  search: string;
};

const SelectAllCheck = ({ search }: SelectAllCheckProps) => {
  const { t } = useTranslation();
  const { control, setValue } = useFormContext();
  const watchedFields = useWatch({ control }) as
    | {
        [key: string]: ExportField | ExportGroup;
      }
    | undefined;

  const isAllChecked = watchedFields
    ? Object.values(watchedFields).every((f) => {
        if (f.type !== "group") {
          return !!f?.checked;
        }
        return f.fields.every((field) => !!field?.checked);
      }) && Object.values(watchedFields).length > 0
    : false;

  const onToggleAll = (checked: boolean) => {
    Object.values(watchedFields ?? {}).forEach((field, i) => {
      if (field.type !== "group") {
        if (!isInSearch(search, t(extractLabel(field)))) return;
        setValue(`${i}.checked`, checked, { shouldDirty: true });
        return;
      }
      field.fields.forEach((field, j) => {
        if (!isInSearch(search, t(extractLabel(field)))) return;
        setValue(`${i}.fields.${j}.checked`, checked, {
          shouldDirty: true,
        });
      });
    });
  };

  return (
    <div className="flex items-center gap-2">
      <Checkbox checked={isAllChecked} onCheckedChange={onToggleAll} />
      <p className="text-sm font-medium text-gray-700">{t("selectAll")}</p>
    </div>
  );
};
